import type { HTMLAttributes } from 'react';
import { cx } from '@/lib/cx';
import styles from './Spinner.module.css';

/**
 * Lifted from `@parrot/ui` (libs/ui/src/Spinner) — see docs/AUDIT_FE_STANDARDS.md
 * for why a copy rather than a dependency.
 *
 * Unmodified except for the `cx` import path. Two behavioural changes from the
 * hand-rolled version it replaces, both deliberate:
 *
 * - It carries `role="status"` and an accessible label. The old one was a bare
 *   decorative `<span>`, so a screen reader was told nothing was happening.
 * - It inherits `currentColor` instead of painting itself `--accent-primary`.
 *   Colour is now the caller's, set on a parent — which is why it reads
 *   correctly inside a button, on a card, or against the canvas without a
 *   crimson dot appearing somewhere it was never designed for.
 *
 * `xs` is new; `sm | md | lg` carry over. The default changed from `md` to
 * `sm`, which is upstream's — every call site here passes `size` explicitly,
 * so nothing moved.
 */

type Props = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
  /** `xs` sits inside a button, `lg` anchors a full-page wait. */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * Announced while the spinner is visible. Pass `undefined` when something
   * adjacent already describes the wait, so it isn't read out twice.
   */
  label?: string;
};

const SPOKES = [0, 1, 2, 3, 4, 5, 6, 7];

/** Indeterminate progress. Inherits `currentColor`, so set the colour on a parent. */
export const Spinner = ({ size = 'sm', label = 'Loading', className, ...spanProps }: Props) => (
  <span
    role={'status'}
    aria-label={label}
    className={cx(styles.base, styles[size], className)}
    {...spanProps}
  >
    {SPOKES.map((spoke) => (
      <span key={spoke} className={styles.spoke} />
    ))}
  </span>
);
